<script lang="ts" setup>
import { ref } from 'vue';
import { useComboboxName } from '../../composables/useComboboxName';
import { useLocale } from '../../composables/useLocale';
import { computed } from 'vue';
import { abortablePromise, createDebounce } from '../../utils';
import { useSearchUsers } from '@ilteducation/customer-mgmt';
import { useConfigHelper } from '@ilteducation/config';
import { VAutocomplete, VDivider, VListItem, VListSubheader, VTextField } from 'vuetify/components';
import {
  useDefaultListRouteQuery,
  useDefaultOrganizationRouteQuery,
  useSelectedEntityRouteQuery,
} from '../../composables/default';
import { useCurrentOrganization } from '../../composables/useCurrentOrganization';
import { useSideBar } from '../../composables/useSideBar';

type Props = {
  listType: string;
};
defineProps<Props>();

const { t } = useLocale();
const { getLocale } = useConfigHelper();

type Emits = {
  (e: 'on-search-change', value: string): void;
};
const emit = defineEmits<Emits>();
const model = defineModel<string>();

const debounce = createDebounce(500);

const { searchUsers } = useSearchUsers();
const { organizations } = useCurrentOrganization();
const { openSideBar } = useSideBar();

const loading = ref<boolean>(false);

type UserItem = { type: 'user'; title: string; value: string; subtitle: string; userUid: string; orgUid: string };
type Item = { type: 'divider' } | { type: 'subheader'; title: string; value: string } | UserItem;

const items = ref<Item[]>();
let abortSearch: () => void;
const search = (word?: string | null) => {
  debounce(async () => {
    if (!word || word.length < 3) {
      items.value = [];
      return;
    }

    loading.value = true;
    abortSearch?.();

    let { abort, promise } = abortablePromise(
      searchUsers({
        q: word,
        filters: { organizations: { uids: organizations.value.map((o) => o.uid) } },
        pageSize: 50,
      }),
    );
    abortSearch = abort;
    const response = await promise;

    type GroupedUsers = {
      value: string;
      title: string;
      users: UserItem[];
    };
    const usersByOrganizations = response.data.reduce<GroupedUsers[]>((acc, user) => {
      user.associations.forEach((assoc) => {
        const orgUid = assoc.organizationUid;
        const orgName = assoc.organization.name;
        let org = acc.find((a) => a.value === orgUid);
        if (!org) {
          org = { value: orgUid, title: orgName, users: [] };
          acc.push(org);
        }
        org.users.push({
          type: 'user',
          title: user.fullname,
          value: `${orgUid}-${user.uid}`,
          subtitle: `${t(`lcp.role_${assoc.role}`)} ∙ ${user.username}`,
          userUid: user.uid,
          orgUid,
        });
      });
      return acc;
    }, []);

    usersByOrganizations.sort((a, b) => a.title.localeCompare(b.title, getLocale()));

    items.value = usersByOrganizations.reduce<Item[]>((acc, curr) => {
      acc.push({ type: 'subheader', title: curr.title, value: curr.value });
      acc.push(...curr.users.sort((a, b) => a.title.localeCompare(b.title, getLocale())));
      acc.push({ type: 'divider' });
      return acc;
    }, []);

    loading.value = false;
  });
};

const searchMenuProps = useComboboxName(() => t('lcp.search_across_organizations'));

const searchOptions = computed(() => [
  {
    title: t('lcp.search_in_current_organization'),
    value: 0,
    is: 'textfield',
  },
  {
    title: t('lcp.search_in_across_organizations'),
    value: 1,
    is: 'autocomplete',
  },
]);

const selectedSearchType = ref([0]);

const params = computed(() => {
  const target = selectedSearchType.value[0];
  const p = searchOptions.value.find((o) => o.value === target) ?? searchOptions.value[0];

  return {
    is: p.is,
  };
});

const { setValue: setSelectedListProperties } = useDefaultListRouteQuery();
const { setValue: setOrganizationQueryParam } = useDefaultOrganizationRouteQuery();
const { setValue: setSelectedEntity } = useSelectedEntityRouteQuery();

const onOrganizationUserClick = (userUid: string, orgUid: string) => {
  setSelectedListProperties('users');
  setOrganizationQueryParam(orgUid);
  setSelectedEntity(userUid);
  openSideBar('user', userUid);
};

const onSearchChange = (text?: string) => {
  model.value = text ?? '';
  emit('on-search-change', model.value);
};

const onSearchTypeChange = () => {
  onSearchChange('');
};
</script>

<template>
  <template v-if="listType === 'users' && params.is === 'autocomplete'">
    <v-autocomplete
      :label="t('lcp.search_across_organizations')"
      :aria-label="t('lcp.search_across_organizations')"
      :menu-props="searchMenuProps"
      :loading="loading"
      no-filter
      rounded="lg"
      density="comfortable"
      :items="items"
      full-width
      menu-icon=""
      single-line
      :clearable="true"
      :persistent-clear="true"
      hide-details
      return-object
      hide-no-data
      :list-props="{
        style: {
          padding: '0px',
        },
      }"
      @update:search="search"
    >
      <template #append-inner>
        <v-icon icon="mdi-domain"></v-icon>
        <v-menu>
          <template v-slot:activator="{ props }">
            <v-icon icon="mdi-menu-down" v-bind="props" :aria-label="t('lcp.search_options')"></v-icon>
          </template>
          <v-list
            v-model:selected="selectedSearchType"
            :items="searchOptions"
            @update:selected="onSearchTypeChange"
          ></v-list>
        </v-menu>
      </template>

      <template #item="{ item, props }">
        <VDivider v-if="item.raw.type === 'divider'" />
        <v-list-subheader
          v-else-if="item.raw.type === 'subheader'"
          style="z-index: 1"
          sticky
          class="text-uppercase bg-surface"
        >
          <span class="mdi mdi-town-hall"></span>
          {{ props.title }}
        </v-list-subheader>
        <v-list-item
          v-bind="props"
          v-else-if="item.raw.type === 'user'"
          :title="item.title"
          :subtitle="item.raw.subtitle"
          :value="item.value"
          @click="onOrganizationUserClick(item.raw.userUid, item.raw.orgUid)"
        />
      </template>
    </v-autocomplete>
  </template>

  <template v-else>
    <v-text-field
      v-model="model"
      :label="t('lcp.search') + ' ' + t('lcp.' + listType)"
      :aria-label="t('lcp.search') + ' ' + t('lcp.' + listType)"
      rounded="lg"
      density="comfortable"
      full-width
      single-line
      :clearable="true"
      :persistent-clear="true"
      hide-details
      @click:clear="onSearchChange('')"
      @keydown.enter="onSearchChange(model)"
      @change="onSearchChange(model)"
    >
      <template #append-inner>
        <v-icon icon="mdi-magnify" :aria-label="t('lcp.search')" @click="onSearchChange(model)"></v-icon>
        <v-menu v-if="listType === 'users'">
          <template v-slot:activator="{ props }">
            <v-icon icon="mdi-menu-down" v-bind="props" :aria-label="t('lcp.search_options')"></v-icon>
          </template>
          <v-list
            v-model:selected="selectedSearchType"
            :items="searchOptions"
            @update:selected="onSearchTypeChange"
          ></v-list>
        </v-menu>
      </template>
    </v-text-field>
  </template>
</template>
